import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Plus, MessageSquare, Trash2, Circle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

export default function ChatTab({ event }) {
  const { user } = useAuth();
  const eventId = event.event_id;

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [presence, setPresence] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const endRef = useRef(null);
  const typingTimerRef = useRef(null);

  const activeThread = useMemo(() => threads.find((t) => t.thread_id === activeId), [threads, activeId]);

  const loadThreads = async () => {
    const { data } = await api.get(`/events/${eventId}/chat-threads`);
    setThreads(data);
    if (!activeId && data.length > 0) setActiveId(data[0].thread_id);
  };

  const loadMessages = async (tid) => {
    if (!tid) return;
    const { data } = await api.get(`/chat/${tid}`);
    setMsgs(data);
  };

  const loadPresence = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/presence`);
      setPresence(data);
    } catch { /* ignore */ }
  };

  const ping = async (typing = false) => {
    try {
      await api.post(`/presence/ping`, { event_id: eventId, thread_id: activeId, typing });
    } catch { /* ignore */ }
  };

  // Initial load + polling
  useEffect(() => { loadThreads(); }, [eventId]);
  useEffect(() => { loadMessages(activeId); }, [activeId]);
  useEffect(() => {
    if (!activeId) return;
    const msgI = setInterval(() => loadMessages(activeId), 4000);
    const presI = setInterval(() => { loadPresence(); ping(false); }, 10000);
    loadPresence();
    ping(false);
    return () => { clearInterval(msgI); clearInterval(presI); };
  }, [activeId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const handleTyping = (val) => {
    setText(val);
    if (!activeId) return;
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    ping(true);
    typingTimerRef.current = setTimeout(() => ping(false), 4000);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    await api.post(`/chat/${activeId}`, { text, event_id: eventId });
    setText("");
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    loadMessages(activeId);
    loadThreads();
  };

  const createThread = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const { data } = await api.post(`/events/${eventId}/chat-threads`, { name: newName.trim() });
      setNewName("");
      setShowNew(false);
      await loadThreads();
      setActiveId(data.thread_id);
      toast.success(`Thread "${data.name}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create thread");
    }
  };

  const deleteThread = async (tid) => {
    if (!window.confirm("Delete this thread and all its messages?")) return;
    try {
      await api.delete(`/chat-threads/${tid}`);
      toast.success("Thread deleted");
      if (activeId === tid) setActiveId(null);
      await loadThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const othersOnline = presence.filter((p) => p.user_id !== user.user_id);
  const typingNow = othersOnline.filter((p) => p.typing_in === activeId);

  return (
    <div className="dashboard-card p-0 max-w-5xl mx-auto flex" style={{ height: 560 }} data-testid="chat-tab">
      {/* Thread Sidebar */}
      <aside className="w-64 border-r border-slate-100 flex flex-col">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-bold text-sm">Threads</div>
          <button
            onClick={() => setShowNew((v) => !v)}
            className="text-orange-600 hover:bg-orange-50 rounded-full p-1.5"
            data-testid="chat-new-thread-btn"
            title="New thread"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {showNew && (
          <form onSubmit={createThread} className="p-3 border-b border-slate-100 space-y-2 bg-orange-50/30">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Thread name…"
              className="input-pill text-sm"
              data-testid="chat-new-thread-input"
            />
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-xs py-1.5 flex-1" data-testid="chat-new-thread-submit">Create</button>
              <button type="button" onClick={() => { setShowNew(false); setNewName(""); }} className="btn-ghost text-xs py-1.5 flex-1">Cancel</button>
            </div>
          </form>
        )}
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 && <div className="p-4 text-xs text-slate-400 text-center">No threads yet</div>}
          {threads.map((t) => {
            const active = t.thread_id === activeId;
            const someoneTyping = othersOnline.some((p) => p.typing_in === t.thread_id);
            return (
              <button
                key={t.thread_id}
                onClick={() => setActiveId(t.thread_id)}
                className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 ${active ? "bg-orange-50" : ""}`}
                data-testid={`chat-thread-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageSquare className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-orange-600" : "text-slate-400"}`} />
                    <span className="font-semibold text-sm truncate">{t.name}</span>
                  </div>
                  {(t.created_by === user.user_id || user.role === "admin") && t.name !== "General" && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); deleteThread(t.thread_id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); deleteThread(t.thread_id); } }}
                      className="text-slate-300 hover:text-rose-500 cursor-pointer"
                      title="Delete thread"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                  <span>{t.message_count} msg</span>
                  {someoneTyping && <span className="text-orange-600 italic">typing…</span>}
                </div>
                {t.last_message && (
                  <div className="text-xs text-slate-500 truncate mt-1">
                    <span className="font-medium">{t.last_message.sender_name?.split(" ")[0]}:</span> {t.last_message.text}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Message Pane */}
      <div className="flex-1 flex flex-col">
        <header className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg" data-testid="chat-active-thread">{activeThread?.name || "Select a thread"}</h3>
            <p className="text-xs text-slate-500">Visible to PM, Regional PM, School Principal & Admin.</p>
          </div>
          {othersOnline.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="chat-presence-list">
              {othersOnline.slice(0, 5).map((p) => (
                <span key={p.user_id} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">
                  <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                  {p.user_name.split(" ")[0]}
                </span>
              ))}
              {othersOnline.length > 5 && <span className="text-slate-400">+{othersOnline.length - 5}</span>}
            </div>
          )}
        </header>

        <div className="flex-1 p-4 overflow-y-auto bg-slate-50/30">
          {!activeId && <div className="text-center text-slate-400 text-sm mt-12">Pick or create a thread.</div>}
          {activeId && msgs.length === 0 && <div className="text-center text-slate-400 text-sm mt-12">No messages yet.</div>}
          {msgs.map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.message_id} className={`mb-3 flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${mine ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="text-xs opacity-80 mb-0.5">{m.sender_name} · {m.sender_role}</div>
                  <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            );
          })}
          {typingNow.length > 0 && (
            <div className="text-xs text-slate-500 italic ml-2 mt-1" data-testid="chat-typing-indicator">
              {typingNow.map((p) => p.user_name.split(" ")[0]).join(", ")} {typingNow.length === 1 ? "is" : "are"} typing…
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="p-4 border-t border-slate-100 flex gap-2">
          <input
            value={text}
            onChange={(e) => handleTyping(e.target.value)}
            placeholder={activeId ? "Type a message…" : "Select a thread first"}
            disabled={!activeId}
            className="input-pill"
            data-testid="chat-input"
          />
          <button type="submit" disabled={!activeId || !text.trim()} className="btn-primary px-5 disabled:opacity-40" data-testid="chat-send">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
